import uniq from 'lodash/uniq'

import asyncAction, { isSuccessful } from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import { type StatsContent } from 'modules/shift_management/hooks/reducers/schedule'
import { useFilters } from 'modules/shift_management/hooks/useFilters'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import Api from 'modules/shift_management/lib/Api'
import type { Filters } from 'modules/shift_management/lib/types'
import type { ShiftsRow, View } from 'modules/shift_management/lib/types'
import ShiftsConflicts from 'modules/shift_management/store/internal/collections/ShiftsConflicts'

import { EnabledFeaturesState } from '../reducers/enabledFeatures'
import { useEnabledFeatures } from '../useEnabledFeatures'
import { useShiftsRows } from '../useShiftsRows'
import { useUI } from '../useUI'

interface Result {
  shiftsRows: ShiftsRow[]
  stats: StatsContent
}

/**
 * When fetching all the employees that have changed after adding a shift, we
 * might face that we need to refetch more than 1 page of employees. This
 * function takes care of the pagination
 */
const fetchShiftRows = async (
  view: View,
  filters: Filters,
  employeeIds: number[],
  features: EnabledFeaturesState,
  contractVersions: ContractVersions
) => {
  let page = 1
  const { startAt, endAt, groupType } = view
  let shiftsRowsToRefresh: ShiftsRow[] = []
  let result: Result = {
    shiftsRows: [],
    stats: {
      shiftConflicts: new ShiftsConflicts([]),
      hasDrafts: false,
      hasDraftsWithoutConflict: false,
    },
  }

  do {
    // TODO: After adding new shifts, we don't need to fetch leaves, planning_versions,
    // etc of the employees as they are already loaded. In order to avoid it, I would
    // create another method into Api that only fetches /employees_permissions
    const [{ shiftsRows }, { stats }] = await Promise.all([
      Api.fetch(
        { page, startAt, endAt, filters, groupType, features, employeeIds },
        contractVersions
      ),
      Api.fetchStats({ startAt, endAt, filters, view, employeeIds }),
    ])
    const statsResult = {
      hasDrafts: stats.has_drafts,
      hasDraftsWithoutConflict: stats.has_drafts_without_conflict,
      shiftConflicts: ShiftsConflicts.buildFromProps(stats.shift_conflicts || []),
    }
    result = { shiftsRows, stats: statsResult }
    shiftsRowsToRefresh = [...shiftsRowsToRefresh, ...result.shiftsRows]
    page += 1
  } while (result.shiftsRows.length === 30 && view.isByEmployees)

  return { ...result, shiftsRows: shiftsRowsToRefresh }
}

const useRefreshShiftsRows = (contractVersions: ContractVersions) => {
  const [, dispatchShiftsRows] = useShiftsRows()
  const [filters] = useFilters()
  const [{ view }, dispatchSchedule] = useSchedule()
  const [features] = useEnabledFeatures()
  const [, dispatchUI] = useUI()

  const refreshShiftsRows = async (shiftsRows: ShiftsRow[], employeeIds: number[] = []) => {
    const employeeIdsFromShiftsRows = shiftsRows.flatMap((row: ShiftsRow) => row.employeeIds)
    const requestEmployeeIds = [...employeeIds, ...employeeIdsFromShiftsRows]
    const { shiftsRows: newShiftsRows, stats } = await fetchShiftRows(
      view,
      filters,
      requestEmployeeIds,
      features,
      contractVersions
    )

    dispatchShiftsRows({
      type: 'REFRESH_SHIFTS_ROWS',
      shiftsRows: newShiftsRows,
    })
    dispatchUI({ type: 'NOT_REFRESHING_STATS' })
    dispatchSchedule({
      type: 'REFRESH_STATS',
      previousShiftsRows: shiftsRows,
      shiftsRows: newShiftsRows,
      stats,
    })
  }

  return { refreshShiftsRows }
}

const useRefreshStats = (startAt: string, endAt: string) => {
  const [, dispatchShiftsRows] = useShiftsRows()
  const [filters] = useFilters()
  const [{ view }, dispatchSchedule] = useSchedule()
  const [, dispatchUI] = useUI()

  const refreshStats = async (shiftsRows: ShiftsRow[]) => {
    const employeeIds = uniq(shiftsRows.flatMap((row: ShiftsRow) => row.employeeIds))
    const result = await asyncAction(
      () => Api.fetchStats({ startAt, endAt, filters, view, employeeIds }),
      {
        loadingMessage: i18n.t(
          'shift_management.notices.refreshing_stats_notification.description'
        ),
        successFlash: false,
      }
    )

    if (isSuccessful(result)) {
      const { stats } = result.value
      dispatchShiftsRows({
        type: 'REFRESH_SHIFTS_ROWS',
        shiftsRows: [],
      })
      dispatchUI({ type: 'NOT_REFRESHING_STATS' })
      dispatchSchedule({
        type: 'REFRESH_STATS',
        previousShiftsRows: shiftsRows,
        shiftsRows: [],
        stats: {
          hasDrafts: stats.has_drafts,
          hasDraftsWithoutConflict: stats.has_drafts_without_conflict,
          shiftConflicts: ShiftsConflicts.buildFromProps(stats?.shift_conflicts || []),
        },
      })
    }
  }

  return { refreshStats }
}

export { useRefreshShiftsRows, useRefreshStats }
